import type { ButtonPreset, PostButton } from '../../lib/types';
import './ButtonEditor.css';

const PRESETS: { value: ButtonPreset; label: string }[] = [
  { value: 'primary', label: 'Primary' },
  { value: 'outline', label: 'Outline' },
  { value: 'ghost', label: 'Ghost' },
  { value: 'download', label: 'Download' },
  { value: 'link', label: 'Link' },
];

interface Props {
  buttons: PostButton[];
  onChange: (next: PostButton[]) => void;
  disabled?: boolean;
}

/**
 * Add/remove/reorder team-defined link buttons. Each button picks a preset
 * (shown with a small visual sample), a label, and a URL. Emits PostButton[].
 */
export function ButtonEditor({ buttons, onChange, disabled }: Props) {
  function update(i: number, fields: Partial<PostButton>) {
    onChange(buttons.map((b, idx) => (idx === i ? { ...b, ...fields } : b)));
  }
  function add() {
    onChange([...buttons, { preset: 'primary', label: '', url: '' }]);
  }
  function remove(i: number) {
    onChange(buttons.filter((_, idx) => idx !== i));
  }
  function move(i: number, dir: -1 | 1) {
    const to = i + dir;
    if (to < 0 || to >= buttons.length) return;
    const next = [...buttons];
    [next[i], next[to]] = [next[to], next[i]];
    onChange(next);
  }

  return (
    <div className="btned">
      {buttons.length === 0 && (
        <p className="btned__empty">No buttons yet. Add a call-to-action link.</p>
      )}

      {buttons.map((b, i) => (
        <div key={i} className="btned__row">
          <div className="btned__presets" role="radiogroup" aria-label="Button style">
            {PRESETS.map((p) => (
              <label
                key={p.value}
                className={`btned__preset ${b.preset === p.value ? 'btned__preset--on' : ''}`}
                title={p.label}
              >
                <input
                  type="radio"
                  name={`btn-preset-${i}`}
                  value={p.value}
                  checked={b.preset === p.value}
                  onChange={() => update(i, { preset: p.value })}
                  disabled={disabled}
                />
                <span className={`btned__sample btned__sample--${p.value}`}>
                  {p.value === 'download' ? '↓ ' : ''}{p.label}
                </span>
              </label>
            ))}
          </div>

          <div className="btned__fields">
            <input
              type="text"
              className="btned__label"
              placeholder="Button label"
              maxLength={60}
              value={b.label}
              onChange={(e) => update(i, { label: e.target.value })}
              disabled={disabled}
            />
            <input
              type="url"
              className="btned__url"
              placeholder="https://…"
              value={b.url}
              onChange={(e) => update(i, { url: e.target.value })}
              disabled={disabled}
            />
          </div>

          <div className="btned__actions">
            <button
              type="button"
              className="btned__act"
              onClick={() => move(i, -1)}
              disabled={disabled || i === 0}
              aria-label="Move up"
              title="Move up"
            >
              ↑
            </button>
            <button
              type="button"
              className="btned__act"
              onClick={() => move(i, 1)}
              disabled={disabled || i === buttons.length - 1}
              aria-label="Move down"
              title="Move down"
            >
              ↓
            </button>
            <button
              type="button"
              className="btned__act btned__act--remove"
              onClick={() => remove(i)}
              disabled={disabled}
              aria-label="Remove button"
              title="Remove button"
            >
              ×
            </button>
          </div>
        </div>
      ))}

      <button
        type="button"
        className="btn btn--ghost btn--sm btned__add"
        onClick={add}
        disabled={disabled}
      >
        + Add button
      </button>
    </div>
  );
}

/** Drop blank rows + trim before publishing. */
export function cleanButtons(buttons: PostButton[]): PostButton[] {
  return buttons
    .map((b) => ({ preset: b.preset, label: b.label.trim(), url: b.url.trim() }))
    .filter((b) => b.label.length > 0 && b.url.length > 0);
}
